/**
 * Product Header – layout, SVG graphics, and PDF text rendering
 *
 * Single module for the Druckplan product header:
 * - Layout and coordinates (PlanTemplate.svg)
 * - SVG: frame + grid (used by pdfme for the graphics layer)
 * - PDF: text via pdf-lib (custom schema for Bold/correct fonts)
 *
 * Frame transform: matrix(3.543307,0,0,3.543307,2.9527661,1058.5433) with scale(1,-1)
 * Formula: x' = 3.543307 * x + 2.9527661, y' = -3.543307 * y + 1058.5433
 */

import {
  type FrameSpec,
  generateDecorativeFrame,
  type TransformFunctions,
} from "./decorativeFrame";

// =============================================================================
// TYPES
// =============================================================================

export interface ProductHeaderData {
  group: string;
  sb: string;
  date: string;
  archive: string;
  title: string;
  afsId: string;
  orderId: string;
  customer: string;
  variant: string;
  alukante: string;
  profilstaerke: string;
  muster: string;
  price: string;
  partsList: Array<{
    count: string;
    description: string;
    articleNumber?: string;
  }>;
  objectWidth: string;
  objectDepth: string;
  objectHeight: string;
  packSize: string;
  outerSize: string;
  caseType: string;
  model: string;
}

/** All layout positions in content coordinates: y increases downward (top = smaller y, bottom = larger y), x increases rightward. */
export interface TableLayout {
  frame: FrameSpec;
  inner: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  partsArea: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    countColEnd: number;
    descColEnd: number;
  };
  rightArea: {
    left: number;
    right: number;
    /** Content y of the bottom edge of the dimensions (B/T/H) section. */
    dimensionsBottom: number;
    /** Content y of the bottom edge of the middle section. */
    middleSectionBottom: number;
    /** Content y of the top edge of the Case/CNC/Preis section. */
    caseInfoTop: number;
    /** Content y of the bottom edge of the Case/CNC/Preis section. */
    caseInfoBottom: number;
    dividerX: number;
  };
  bottomArea: {
    /** Content y of the top edge of the bottom area (below parts list). */
    top: number;
    /** Content y of the horizontal divider line in the bottom area. */
    middle: number;
    dividerX: number;
  };
  numRows: number;
  rowHeight: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const HEADER_FONT_SIZE = 10;
export const HEADER_SUBTEXT_FONT_SIZE = 6;
export const HEADER_TITLE_FONT_SIZE = 15;
export const HEADER_SMALL_FONT_SIZE = 6;
export const HEADER_ARTICLE_FONT_SIZE = 10;

const SCALE = 3.543307;
const OFFSET_X = 2.9527661;
const OFFSET_Y = 1058.5433;

const BASE_FRAME: FrameSpec = {
  x: 12.8,
  y: 195.7,
  width: 177,
  height: 89.4,
};

const ROW_HEIGHT = 6;
const MIN_PARTS_ROWS = 10;
const INNER_PADDING = 3.2;
const STROKE_PADDING = 2;

// =============================================================================
// COORDINATE TRANSFORMATION
// =============================================================================
//
// Content coordinates (layout): y increases downward, x rightward.
//   top = smaller y, bottom = larger y; left = smaller x, right = larger x.
// ty(y) inverts y for SVG: ty(y) = -SCALE*y + OFFSET_Y → larger content y → smaller SVG y.
// In the generated SVG, smaller SVG y is drawn higher. So when drawing a vertical line
// from content y1 (top) to y2 (bottom): ty(y1) > ty(y2), so the line runs from
// (x, large SVG y) to (x, small SVG y) = from lower to higher on the page.
// For the Preis divider we use preisDividerPdfTop / preisDividerPdfBottom to name
// the content y of the end that appears at the top resp. bottom of the line on the PDF.

export function tx(x: number): number {
  return SCALE * x + OFFSET_X;
}

export function ty(y: number): number {
  return -SCALE * y + OFFSET_Y;
}

export function ts(s: number): number {
  return SCALE * s;
}

const frameTransform: TransformFunctions = { tx, ty, ts };

const FRAME_LEFT_SVG = tx(BASE_FRAME.x);
const FRAME_RIGHT_SVG = tx(BASE_FRAME.x + BASE_FRAME.width);
const FRAME_WIDTH_SVG = FRAME_RIGHT_SVG - FRAME_LEFT_SVG;
const VIEWBOX_X = Math.floor(FRAME_LEFT_SVG - STROKE_PADDING);
const VIEWBOX_WIDTH = Math.ceil(FRAME_WIDTH_SVG + 2 * STROKE_PADDING);

// =============================================================================
// LAYOUT
// =============================================================================

function calculateFrameSpec(numParts: number): FrameSpec {
  const numRows = Math.max(MIN_PARTS_ROWS, numParts);
  const extraRows = numRows - MIN_PARTS_ROWS;
  const extraHeight = extraRows * ROW_HEIGHT;
  return { ...BASE_FRAME, height: BASE_FRAME.height + extraHeight };
}

export function calculateLayout(numParts: number): TableLayout {
  const numRows = Math.max(MIN_PARTS_ROWS, numParts);
  const frame = calculateFrameSpec(numParts);

  const innerLeft = frame.x + INNER_PADDING;
  const innerRight = frame.x + frame.width - INNER_PADDING;
  const innerTop = frame.y + INNER_PADDING;
  const innerBottom = frame.y + frame.height - INNER_PADDING;
  const partsBottom = innerTop + numRows * ROW_HEIGHT;
  const rightAreaLeft = frame.x + 100;

  return {
    frame,
    inner: {
      left: innerLeft,
      right: innerRight,
      top: innerTop,
      bottom: innerBottom,
    },
    partsArea: {
      left: innerLeft,
      right: rightAreaLeft,
      top: innerTop,
      bottom: partsBottom,
      countColEnd: frame.x + 12,
      descColEnd: frame.x + 60,
    },
    rightArea: {
      left: rightAreaLeft,
      right: innerRight,
      dimensionsBottom: innerTop + 9,
      middleSectionBottom: innerTop + 30,
      caseInfoTop: innerTop + 36,
      caseInfoBottom: partsBottom,
      dividerX: frame.x + 148.2,
    },
    bottomArea: {
      top: partsBottom,
      middle: partsBottom + 6,
      dividerX: rightAreaLeft,
    },
    numRows,
    rowHeight: ROW_HEIGHT,
  };
}

// =============================================================================
// SVG GRAPHICS (frame + grid)
// =============================================================================

function generateCaseFrameGraphics(layout: TableLayout): string {
  return generateDecorativeFrame(frameTransform, layout.frame);
}

function generateInternalGridLines(layout: TableLayout): string {
  const lines: string[] = [];
  const sw = ts(0.1);
  const swGray = ts(0.09);
  const { partsArea, rightArea, bottomArea, inner, numRows, rowHeight } =
    layout;

  // Vertical lines
  lines.push(
    `<line x1="${tx(partsArea.countColEnd).toFixed(2)}" y1="${ty(partsArea.top).toFixed(2)}" x2="${tx(partsArea.countColEnd).toFixed(2)}" y2="${ty(partsArea.bottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(partsArea.descColEnd).toFixed(2)}" y1="${ty(partsArea.top).toFixed(2)}" x2="${tx(partsArea.descColEnd).toFixed(2)}" y2="${ty(partsArea.bottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(partsArea.descColEnd).toFixed(2)}" y1="${ty(bottomArea.top).toFixed(2)}" x2="${tx(partsArea.descColEnd).toFixed(2)}" y2="${ty(bottomArea.middle).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(partsArea.top).toFixed(2)}" x2="${tx(rightArea.left).toFixed(2)}" y2="${ty(partsArea.bottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(bottomArea.dividerX).toFixed(2)}" y1="${ty(bottomArea.top).toFixed(2)}" x2="${tx(bottomArea.dividerX).toFixed(2)}" y2="${ty(bottomArea.middle).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(layout.frame.x + 140.2).toFixed(2)}" y1="${ty(bottomArea.middle).toFixed(2)}" x2="${tx(layout.frame.x + 140.2).toFixed(2)}" y2="${ty(inner.bottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(layout.frame.x + 140.2).toFixed(2)}" y1="${ty(partsArea.top).toFixed(2)}" x2="${tx(layout.frame.x + 140.2).toFixed(2)}" y2="${ty(rightArea.dimensionsBottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  // Vertical divider left of "Preis". ty() inverts y: smaller SVG y = drawn higher.
  // So y2 (smaller content y) is the top of the line on PDF, y1 the bottom. To make
  // the line start one cell lower (PDF top moves down), use a smaller content y for the top end.
  const preisDividerPdfTop = bottomArea.middle - rowHeight; // line ends one row lower (top on PDF)
  const preisDividerPdfBottom = rightArea.caseInfoTop; // bottom of line on PDF
  lines.push(
    `<line x1="${tx(rightArea.dividerX).toFixed(2)}" y1="${ty(preisDividerPdfBottom).toFixed(2)}" x2="${tx(rightArea.dividerX).toFixed(2)}" y2="${ty(preisDividerPdfTop).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );

  // Horizontal – parts list rows (gray)
  for (let i = 1; i < numRows; i++) {
    const y = partsArea.top + i * rowHeight;
    lines.push(
      `<line x1="${tx(partsArea.left).toFixed(2)}" y1="${ty(y).toFixed(2)}" x2="${tx(partsArea.right).toFixed(2)}" y2="${ty(y).toFixed(2)}" stroke="#000" stroke-width="${swGray.toFixed(2)}"/>`,
    );
  }

  const rightSectionStart = rightArea.caseInfoTop;
  const rightSectionEnd = partsArea.bottom;
  const rightRowCount = Math.floor(
    (rightSectionEnd - rightSectionStart) / rowHeight,
  );
  for (let i = 1; i < rightRowCount; i++) {
    const y = rightSectionStart + i * rowHeight;
    if (y < rightSectionEnd) {
      lines.push(
        `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(y).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(y).toFixed(2)}" stroke="#000" stroke-width="${swGray.toFixed(2)}"/>`,
      );
    }
  }

  lines.push(
    `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(partsArea.bottom).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(partsArea.bottom).toFixed(2)}" stroke="#000" stroke-width="${swGray.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(partsArea.left).toFixed(2)}" y1="${ty(bottomArea.middle + 8).toFixed(2)}" x2="${tx(layout.frame.x + 140.2).toFixed(2)}" y2="${ty(bottomArea.middle + 8).toFixed(2)}" stroke="#000" stroke-width="${swGray.toFixed(2)}"/>`,
  );

  // Strong dividers
  lines.push(
    `<line x1="${tx(partsArea.left).toFixed(2)}" y1="${ty(partsArea.bottom).toFixed(2)}" x2="${tx(partsArea.right).toFixed(2)}" y2="${ty(partsArea.bottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(partsArea.left).toFixed(2)}" y1="${ty(bottomArea.middle).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(bottomArea.middle).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(rightArea.dimensionsBottom).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(rightArea.dimensionsBottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(rightArea.middleSectionBottom).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(rightArea.middleSectionBottom).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );
  lines.push(
    `<line x1="${tx(rightArea.left).toFixed(2)}" y1="${ty(rightArea.caseInfoTop).toFixed(2)}" x2="${tx(rightArea.right).toFixed(2)}" y2="${ty(rightArea.caseInfoTop).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  );

  return lines.join("\n  ");
}

/** Build SVG graphics only (frame + grid). Text is rendered by the PDF schema. */
export function buildProductHeaderSvgGraphicsOnly(
  data: ProductHeaderData,
): string {
  const layout = calculateLayout(data.partsList.length);
  const extraHeight =
    Math.max(0, data.partsList.length - MIN_PARTS_ROWS) * ROW_HEIGHT;
  const viewBoxHeight = 340 + ts(extraHeight);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEWBOX_X.toFixed(0)} 29 ${VIEWBOX_WIDTH.toFixed(0)} ${viewBoxHeight.toFixed(0)}" preserveAspectRatio="none">
  ${generateCaseFrameGraphics(layout)}
  ${generateInternalGridLines(layout)}
</svg>`;
}

// =============================================================================
// PDF TEXT (pdf-lib)
// =============================================================================
// Text positions use the same viewBox space as the SVG: svgToPdf(svgX, svgY) with
// viewBox y origin 29; smaller svgY = higher on the page, larger svgY = lower.

const MM_TO_PT = 2.83465;

async function drawProductHeader(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string;
}): Promise<void> {
  const { rgb, StandardFonts } = await import("pdf-lib");

  let data: ProductHeaderData;
  try {
    data = JSON.parse(arg.value);
  } catch {
    return;
  }

  const layout = calculateLayout(data.partsList.length);
  const { x, y } = arg.schema.position;
  const { width, height } = arg.schema;
  const pageHeight = arg.page.getHeight();

  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT;

  const font = await arg.pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await arg.pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const extraHeight =
    Math.max(0, data.partsList.length - MIN_PARTS_ROWS) * ROW_HEIGHT;
  const extraHeightOffset = ts(extraHeight);

  const svgViewBoxHeight = 340 + ts(extraHeight);
  const pdfWidth = width * MM_TO_PT;
  const pdfHeight = height * MM_TO_PT;
  const scaleX = pdfWidth / VIEWBOX_WIDTH;
  const scaleY = pdfHeight / svgViewBoxHeight;

  const svgToPdf = (svgX: number, svgY: number): { x: number; y: number } => {
    const relativeX = svgX - VIEWBOX_X;
    const relativeY = svgY - 29;
    return {
      x: startX + relativeX * scaleX,
      y: startY - relativeY * scaleY,
    };
  };

  const leftCol = tx(layout.inner.left) + 5;
  const rightCol = FRAME_LEFT_SVG + 500;
  const frameRightInner = tx(layout.rightArea.left);
  const specsCol2 = tx(layout.bottomArea.dividerX) + 5;
  const specsCol3 = tx(layout.partsArea.descColEnd) + 5;
  const specsCol4 = tx(layout.rightArea.dividerX) + 5;

  // Row 1: Warengruppe, SB, Datum, Archive
  const row1Pos = svgToPdf(leftCol, 43);
  arg.page.drawText("Warengruppe:", {
    x: row1Pos.x,
    y: row1Pos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.group, {
    x: row1Pos.x + (95 / SCALE) * MM_TO_PT,
    y: row1Pos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const sbPos = svgToPdf(leftCol + 210, 43);
  arg.page.drawText("SB:", {
    x: sbPos.x,
    y: sbPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.sb, {
    x: sbPos.x + (30 / SCALE) * MM_TO_PT,
    y: sbPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const datumPos = svgToPdf(leftCol + 320, 43);
  arg.page.drawText("Datum:", {
    x: datumPos.x,
    y: datumPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.date, {
    x: datumPos.x + (45 / SCALE) * MM_TO_PT,
    y: datumPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const archivePos = svgToPdf(rightCol - 30, 43);
  arg.page.drawText("Archive:", {
    x: archivePos.x,
    y: archivePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.archive, {
    x: archivePos.x + (60 / SCALE) * MM_TO_PT,
    y: archivePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const produktPos = svgToPdf(leftCol, 81.52);
  arg.page.drawText("Produkt:", {
    x: produktPos.x,
    y: produktPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.title, {
    x: produktPos.x + (60 / SCALE) * MM_TO_PT,
    y: produktPos.y,
    size: HEADER_TITLE_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const afsIdPos = svgToPdf(rightCol, 76);
  arg.page.drawText("AFS-ID:", {
    x: afsIdPos.x,
    y: afsIdPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.afsId, {
    x: afsIdPos.x + (50 / SCALE) * MM_TO_PT,
    y: afsIdPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const idPos = svgToPdf(rightCol, 96);
  arg.page.drawText("ID:", {
    x: idPos.x,
    y: idPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.orderId, {
    x: idPos.x + (25 / SCALE) * MM_TO_PT,
    y: idPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const customerPos = svgToPdf(rightCol, 114);
  arg.page.drawText(data.customer, {
    x: customerPos.x,
    y: customerPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const variantePos = svgToPdf(leftCol, 114);
  arg.page.drawText("Variante:", {
    x: variantePos.x,
    y: variantePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.variant, {
    x: variantePos.x + (60 / SCALE) * MM_TO_PT,
    y: variantePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const alukantePos = svgToPdf(leftCol, 136);
  arg.page.drawText("Alukante:", {
    x: alukantePos.x,
    y: alukantePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.alukante, {
    x: alukantePos.x + (70 / SCALE) * MM_TO_PT,
    y: alukantePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const musterPos = svgToPdf(specsCol2, 136);
  arg.page.drawText("Muster", {
    x: musterPos.x,
    y: musterPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("(Quelle)", {
    x: musterPos.x + (45 / SCALE) * MM_TO_PT,
    y: musterPos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(`: ${data.muster}`, {
    x: musterPos.x + ((45 + 30) / SCALE) * MM_TO_PT,
    y: musterPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const profilstaerkePos = svgToPdf(specsCol3, 136);
  arg.page.drawText("Profilstärke:", {
    x: profilstaerkePos.x,
    y: profilstaerkePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.profilstaerke, {
    x: profilstaerkePos.x + (85 / SCALE) * MM_TO_PT,
    y: profilstaerkePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const preisLabelPos = svgToPdf(specsCol4, 156);
  arg.page.drawText("Preis:", {
    x: preisLabelPos.x,
    y: preisLabelPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("(netto EK)", {
    x: preisLabelPos.x + (37 / SCALE) * MM_TO_PT,
    y: preisLabelPos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  const preisValuePos = svgToPdf(specsCol4, 176);
  arg.page.drawText(data.price, {
    x: preisValuePos.x,
    y: preisValuePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const parts = data.partsList.slice(0, layout.numRows);
  parts.forEach((part, i) => {
    const rowY = layout.partsArea.bottom - (i + 1) * layout.rowHeight + 2.5;
    const rowPos = svgToPdf(tx(layout.partsArea.left + 5), ty(rowY));
    const descPos = svgToPdf(tx(layout.partsArea.countColEnd + 2), ty(rowY));
    const articlePos = svgToPdf(tx(layout.partsArea.descColEnd + 2), ty(rowY));

    const countWidth = font.widthOfTextAtSize(part.count, HEADER_FONT_SIZE);
    arg.page.drawText(part.count, {
      x: rowPos.x - countWidth,
      y: rowPos.y,
      size: HEADER_FONT_SIZE,
      font,
      color: rgb(0, 0, 0),
    });
    arg.page.drawText(part.description, {
      x: descPos.x,
      y: descPos.y,
      size: HEADER_FONT_SIZE,
      font,
      color: rgb(0, 0, 0),
    });
    if (part.articleNumber) {
      arg.page.drawText(part.articleNumber, {
        x: articlePos.x,
        y: articlePos.y,
        size: HEADER_ARTICLE_FONT_SIZE,
        font,
        color: rgb(0, 0, 0),
      });
    }
  });

  const bPos = svgToPdf(frameRightInner + 5, 156);
  arg.page.drawText("B", {
    x: bPos.x,
    y: bPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("[mm]", {
    x: bPos.x + (12 / SCALE) * MM_TO_PT,
    y: bPos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(" = ", {
    x: bPos.x + ((8 + 25) / SCALE) * MM_TO_PT,
    y: bPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.objectWidth, {
    x: bPos.x + ((8 + 25 + 20) / SCALE) * MM_TO_PT,
    y: bPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const tPos = svgToPdf(frameRightInner + 5, 177);
  arg.page.drawText("T", {
    x: tPos.x,
    y: tPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("[mm]", {
    x: tPos.x + (12 / SCALE) * MM_TO_PT,
    y: tPos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(" = ", {
    x: tPos.x + ((8 + 25) / SCALE) * MM_TO_PT,
    y: tPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.objectDepth, {
    x: tPos.x + ((8 + 25 + 20) / SCALE) * MM_TO_PT,
    y: tPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const hPos = svgToPdf(frameRightInner + 5, 198);
  arg.page.drawText("H", {
    x: hPos.x,
    y: hPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("[mm]", {
    x: hPos.x + (12 / SCALE) * MM_TO_PT,
    y: hPos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(" = ", {
    x: hPos.x + ((8 + 25) / SCALE) * MM_TO_PT,
    y: hPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.objectHeight, {
    x: hPos.x + ((8 + 25 + 20) / SCALE) * MM_TO_PT,
    y: hPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const caseY = 242 + extraHeightOffset;
  const casePos = svgToPdf(frameRightInner + 5, caseY);
  arg.page.drawText("Case", {
    x: casePos.x,
    y: casePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("(Packmaß)", {
    x: casePos.x + (35 / SCALE) * MM_TO_PT,
    y: casePos.y + (1 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(`: ${data.packSize}`, {
    x: casePos.x + ((35 + 40) / SCALE) * MM_TO_PT,
    y: casePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  const bthText = "(b/t/h)";
  const bthWidth = fontBold.widthOfTextAtSize(
    bthText,
    HEADER_SUBTEXT_FONT_SIZE,
  );
  const bthPos = svgToPdf(FRAME_RIGHT_SVG - 15, caseY);
  arg.page.drawText(bthText, {
    x: bthPos.x - bthWidth,
    y: bthPos.y,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });

  const cncY = 262 + extraHeightOffset;
  const cncPos = svgToPdf(frameRightInner + 5, cncY);
  arg.page.drawText("CNC:", {
    x: cncPos.x,
    y: cncPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.outerSize, {
    x: cncPos.x + (35 / SCALE) * MM_TO_PT,
    y: cncPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const bottomY = 336 + extraHeightOffset;
  const casetypPos = svgToPdf(frameRightInner + 5, bottomY);
  arg.page.drawText("Casetyp:", {
    x: casetypPos.x,
    y: casetypPos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.caseType, {
    x: casetypPos.x + (60 / SCALE) * MM_TO_PT,
    y: casetypPos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("(Grundform)", {
    x: casetypPos.x,
    y: casetypPos.y - (14 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });

  const baureiheX = tx(layout.frame.x + 140.2) + 5;
  const baureihePos = svgToPdf(baureiheX, bottomY);
  arg.page.drawText("Baureihe:", {
    x: baureihePos.x,
    y: baureihePos.y,
    size: HEADER_FONT_SIZE,
    font: fontBold,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText(data.model, {
    x: baureihePos.x + (60 / SCALE) * MM_TO_PT,
    y: baureihePos.y,
    size: HEADER_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
  arg.page.drawText("(Qualität)", {
    x: baureihePos.x,
    y: baureihePos.y - (14 / SCALE) * MM_TO_PT,
    size: HEADER_SUBTEXT_FONT_SIZE,
    font,
    color: rgb(0, 0, 0),
  });
}

// =============================================================================
// PDFME SCHEMA
// =============================================================================

export const productHeaderPdfSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.color = "#666";
    div.style.fontSize = "12px";
    div.textContent = "Product Header";
    arg.rootElement.appendChild(div);
  },

  pdf: drawProductHeader,

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "productHeader",
      type: "productHeaderPdf",
      position: { x: 0, y: 0 },
      width: 190,
      height: 98,
    },
    defaultValue: "{}",
  },
};

export default productHeaderPdfSchema;
